import React, { useEffect, useMemo, useState } from 'react';
import { imageUrl, imageVersionFor } from '../utils/imageUrl';

/** The two tile sizes the boards draw: 24 in the by-line, 28 in the mobile header row. */
const TILE_SIZE_CLASS = {
  sm: 'size-[var(--cs-size-logo-sm)]',
  md: 'size-[var(--cs-size-logo)]',
};

/** Up to two letters from the organisation's name — "Community Foundation" → "CF". */
export function orgInitials(name) {
  if (typeof name !== 'string') return '';
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  const letters = words.length === 1 ? words[0].slice(0, 2) : `${words[0][0]}${words[1][0]}`;
  return letters.toUpperCase();
}

/**
 * The logo URL, resolved exactly as MerchantLogo resolves it: a direct URL wins, else the
 * S3 `<name>_Logo` object. A CharityStack logo is never shown as the organisation's.
 */
function resolveLogoSrc({ logo, merchantName, logoVersion, s3LogoUrl }) {
  if (typeof logo === 'string' && logo.trim() !== '') {
    return logo.toLowerCase().includes('charitystack') ? null : logo;
  }
  if (!merchantName) return null;
  const base = s3LogoUrl || (typeof import.meta !== 'undefined' && import.meta.env?.VITE_S3_LOGO_URL);
  if (!base) return null;
  const constructed = imageUrl({
    base,
    type: 'logo',
    name: `${merchantName}_Logo`,
    version: imageVersionFor({ logoVersion }, { versionField: 'logoVersion' }),
  });
  return constructed.toLowerCase().includes('charitystack') ? null : constructed;
}

/**
 * OrgLogoTile — the organisation identity tile (Paper "Org identity · By line", SPEC §C
 * radius 6 logo tiles). White, 1px edge, radius sm. Shows the organisation's logo when it
 * loads, its initials (11/14 semibold in the brand action role) when there is none or it
 * fails. Replaces the standalone logo row on hosted pages and the overlay.
 *
 * @param {'sm'|'md'} [size='sm'] - 24 (by-line) or 28 (mobile header row).
 */
function OrgLogoTile({ merchantName, logo, logoVersion, s3LogoUrl, size = 'sm' }) {
  const [failed, setFailed] = useState(false);
  const src = useMemo(
    () => resolveLogoSrc({ logo, merchantName, logoVersion, s3LogoUrl }),
    [logo, merchantName, logoVersion, s3LogoUrl],
  );
  useEffect(() => setFailed(false), [src]);

  const initials = orgInitials(merchantName);
  const showImage = Boolean(src) && !failed;
  if (!showImage && !initials) return null;

  return (
    <span
      className={`${TILE_SIZE_CLASS[size] || TILE_SIZE_CLASS.sm} inline-flex shrink-0 items-center justify-center overflow-hidden rounded-[var(--cs-radius-sm)] border border-[color:var(--cs-border-control)] bg-[color:var(--cs-surface-0)]`}
    >
      {showImage ? (
        <img
          src={src}
          alt={merchantName ? `${merchantName} logo` : 'Organization logo'}
          className="h-full w-full object-contain"
          onError={() => setFailed(true)}
          loading="eager"
          decoding="async"
        />
      ) : (
        // CON-528: monogram TEXT reads the brand text role (4.5:1 on white), not the action fill.
        <span
          aria-hidden="true"
          className="text-[length:var(--cs-text-2xs-plus)] leading-[var(--cs-leading-14)] font-semibold text-[color:var(--cs-brand-text,var(--cs-text-primary))]"
        >
          {initials}
        </span>
      )}
    </span>
  );
}

export default OrgLogoTile;
